/** Hue / Saturation / Lightness adjustment — pure math + types,
 *  no React, no Fabric.
 *
 *  v1 ships Master mode only — a single shift applied to every
 *  pixel. Photoshop's per-color-band targeting (Reds / Yellows /
 *  Greens / Cyans / Blues / Magentas) requires per-pixel hue
 *  classification + smoothed band masks; defer until a user asks. */

export interface HueSaturationValue {
  /** Hue shift in degrees, applied modulo 360. -180..+180. */
  hue: number;
  /** Saturation shift, -100 (fully desaturated) to +100 (boosted). */
  saturation: number;
  /** Lightness shift, -100 (toward black) to +100 (toward white). */
  lightness: number;
}

export const identityHueSaturationValue = (): HueSaturationValue => ({
  hue: 0,
  saturation: 0,
  lightness: 0,
});

/** Apply HSL shift to every pixel.
 *
 *  Per-pixel cost is higher than a LUT-based adjustment
 *  (Curves/Levels/B-C) because the transform depends on all three
 *  channels jointly — no shortcut to a 256-entry table. The hot
 *  loop is hand-inlined: no function calls, no tuple allocations,
 *  no per-pixel object literals. The rAF throttle + downscaled
 *  preview pipeline in `AdjustmentBody` keeps slider input smooth
 *  on top of that. */
/** Pure pixel transform — returns a fresh ImageData. Tests use
 *  this variant; the canvas-bound wrapper does the putImageData. */
export function applyHueSaturationPixels(source: ImageData, value: HueSaturationValue): ImageData {
  const w = source.width;
  const h = source.height;
  const sd = source.data;
  const out = new Uint8ClampedArray(sd.length);
  const hueShift = value.hue;
  const satShift = value.saturation / 100;
  const lightShift = value.lightness / 100;
  for (let i = 0; i < sd.length; i += 4) {
    const r = sd[i] / 255;
    const g = sd[i + 1] / 255;
    const b = sd[i + 2] / 255;

    // Inlined RGB → HSL.
    let max = r;
    let min = r;
    if (g > max) max = g;
    if (b > max) max = b;
    if (g < min) min = g;
    if (b < min) min = b;
    let lt = (max + min) * 0.5;
    let hu: number;
    let sa: number;
    if (max === min) {
      hu = 0;
      sa = 0;
    } else {
      const d = max - min;
      sa = lt > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) hu = ((g - b) / d + (g < b ? 6 : 0)) * 60;
      else if (max === g) hu = ((b - r) / d + 2) * 60;
      else hu = ((r - g) / d + 4) * 60;
    }

    // Apply shifts.
    hu = (((hu + hueShift) % 360) + 360) % 360;
    if (satShift >= 0) sa = sa + (1 - sa) * satShift;
    else sa = sa * (1 + satShift);
    if (lightShift >= 0) lt = lt + (1 - lt) * lightShift;
    else lt = lt * (1 + lightShift);

    // Inlined HSL → RGB.
    let rr: number;
    let gg: number;
    let bb: number;
    if (sa === 0) {
      rr = lt;
      gg = lt;
      bb = lt;
    } else {
      const q = lt < 0.5 ? lt * (1 + sa) : lt + sa - lt * sa;
      const p = 2 * lt - q;
      const hk = hu / 360;
      // Three local computations for R, G, B with hk + 1/3, hk, hk - 1/3.
      let tR = hk + 1 / 3;
      let tG = hk;
      let tB = hk - 1 / 3;
      if (tR < 0) tR += 1;
      else if (tR > 1) tR -= 1;
      if (tG < 0) tG += 1;
      else if (tG > 1) tG -= 1;
      if (tB < 0) tB += 1;
      else if (tB > 1) tB -= 1;
      const qmp = q - p;
      rr =
        tR < 1 / 6
          ? p + qmp * 6 * tR
          : tR < 1 / 2
            ? q
            : tR < 2 / 3
              ? p + qmp * (2 / 3 - tR) * 6
              : p;
      gg =
        tG < 1 / 6
          ? p + qmp * 6 * tG
          : tG < 1 / 2
            ? q
            : tG < 2 / 3
              ? p + qmp * (2 / 3 - tG) * 6
              : p;
      bb =
        tB < 1 / 6
          ? p + qmp * 6 * tB
          : tB < 1 / 2
            ? q
            : tB < 2 / 3
              ? p + qmp * (2 / 3 - tB) * 6
              : p;
    }
    out[i] = (rr * 255 + 0.5) | 0;
    out[i + 1] = (gg * 255 + 0.5) | 0;
    out[i + 2] = (bb * 255 + 0.5) | 0;
    out[i + 3] = sd[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyHueSaturationToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: HueSaturationValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyHueSaturationPixels(source, value), 0, 0);
}
